import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, map, combineLatest } from "rxjs";
import {
  DomainViewModel,
  ProcessViewModel,
  UseCaseViewModel,
  FunctionalityViewModel,
  IndustryViewModel,
  AssetCategoryViewModel,
  DemoTileViewModel,
  DemoTemplateViewModel,
  FilterViewModel,
  AssetGroupViewModel,
  MacrosectorViewModel
} from "../../core/model/ContentManagement";
import { DemoAssetService } from "../../services/demo-asset.service";
import { DemoTemplateService } from "../../services/demo-template.service";
import { MetadataService } from "../../services/metadata.service";

export interface FilterValue {
  id: number;
  name: string;
  color: string;
  type: FilterType;
}

enum FilterType {
  macrosector = "macrosector",
  domain = "domain",
  process = "process",
  useCase = "useCase",
  functionality = "functionality",
  industry = "industry",
  category = "category"
}

export const filterColors = {
  macrosector: "#255F85",
  domain: "#32BEF0",
  process: "#0C9995",
  useCase: "#f28b2d",
  functionality: "#6F58C9",
  industry: "#E83943",
  category: "#95C11F"
};

@Injectable({
  providedIn: "root"
})
export class DemoPortfolioService {
  private macrosectorsSubject = new BehaviorSubject<MacrosectorViewModel[]>([]);
  macrosectors$ = this.macrosectorsSubject.asObservable().pipe(
    map(macrosectors => {
      return macrosectors.map(this.mapMacrosector);
    })
  );
  private selectedMacrosectorsSubject = new BehaviorSubject<MacrosectorViewModel[]>([]);
  selectedMacrosectors$ = this.selectedMacrosectorsSubject.asObservable().pipe(
    map(macrosectors => {
      return macrosectors.map(this.mapMacrosector);
    })
  );
  private mapMacrosector(macrosector: MacrosectorViewModel): FilterValue {
    return { id: macrosector.id, name: macrosector.name, color: filterColors.macrosector, type: FilterType.macrosector };
  }

  private domainsSubject = new BehaviorSubject<DomainViewModel[]>([]);
  domains$ = combineLatest([this.domainsSubject.asObservable(), this.selectedMacrosectors$]).pipe(
    map(([domains, selectedMacrosectors]) => {
      return selectedMacrosectors.length
        ? domains.filter(domain => selectedMacrosectors.some(macrosector => macrosector.id === domain.macrosectorId))
        : domains;
    }),
    map(domains => {
      return domains.map(this.mapDomain);
    })
  );
  private selectedDomainsSubject = new BehaviorSubject<DomainViewModel[]>([]);
  selectedDomains$ = this.selectedDomainsSubject.asObservable().pipe(
    map(domains => {
      return domains.map(this.mapDomain);
    })
  );
  private mapDomain(domain: DomainViewModel): FilterValue {
    return { id: domain.id, name: domain.name, color: filterColors.domain, type: FilterType.domain };
  }

  private processSubject = new BehaviorSubject<ProcessViewModel[]>([]);
  process$ = combineLatest([this.processSubject.asObservable(), this.selectedMacrosectors$, this.selectedDomains$]).pipe(
    map(([processes, selectedMacrosectors, selectedDomains]) => {
      if (selectedDomains.length) return processes.filter(p => selectedDomains.some(d => d.id === p.domainId));
      if (selectedMacrosectors.length) return processes.filter(p => selectedMacrosectors.some(m => m.id === p.macrosectorId));
      return processes;
    }),
    map(processes => {
      return processes.map(this.mapProcess);
    })
  );
  private selectedProcessSubject = new BehaviorSubject<ProcessViewModel[]>([]);
  selectedProcess$ = this.selectedProcessSubject.asObservable().pipe(
    map(processes => {
      return processes.map(this.mapProcess);
    })
  );
  private mapProcess(process: ProcessViewModel): FilterValue {
    return { id: process.id, name: process.name, color: filterColors.process, type: FilterType.process };
  }

  private useCasesSubject = new BehaviorSubject<UseCaseViewModel[]>([]);
  useCases$ = combineLatest([
    this.useCasesSubject.asObservable(),
    this.selectedMacrosectors$,
    this.selectedDomains$,
    this.selectedProcess$
  ]).pipe(
    map(([useCases, selectedMacrosectors, selectedDomains, selectedProcess]) => {
      if (selectedProcess.length) return useCases.filter(u => selectedProcess.some(p => p.id === u.processId));
      else if (selectedDomains.length) return useCases.filter(u => selectedDomains.some(d => d.id === u.domainId));
      else if (selectedMacrosectors.length) return useCases.filter(u => selectedMacrosectors.some(m => m.id === u.macrosectorId));
      else return useCases;
    }),
    map(useCases => {
      return useCases.map(this.mapUseCase);
    })
  );
  private selectedUseCasesSubject = new BehaviorSubject<UseCaseViewModel[]>([]);
  selectedUseCases$ = this.selectedUseCasesSubject.asObservable().pipe(
    map(useCases => {
      return useCases.map(this.mapUseCase);
    })
  );
  private mapUseCase(useCase: UseCaseViewModel): FilterValue {
    return { id: useCase.id, name: useCase.name, color: filterColors.useCase, type: FilterType.useCase };
  }

  private functionalitiesSubject = new BehaviorSubject<FunctionalityViewModel[]>([]);
  functionalities$ = this.functionalitiesSubject.asObservable().pipe(
    map(functionalities => {
      return functionalities.map(this.mapFunctionality);
    })
  );
  private selectedFunctionalitiesSubject = new BehaviorSubject<FunctionalityViewModel[]>([]);
  selectedFunctionalities$ = this.selectedFunctionalitiesSubject.asObservable().pipe(
    map(functionalities => {
      return functionalities.map(this.mapFunctionality);
    })
  );
  private mapFunctionality(functionality: FunctionalityViewModel): FilterValue {
    return { id: functionality.id, name: functionality.name, color: filterColors.functionality, type: FilterType.functionality };
  }

  private industriesSubject = new BehaviorSubject<IndustryViewModel[]>([]);
  industries$ = this.industriesSubject.asObservable().pipe(
    map(industries => {
      return industries.map(this.mapIndustry);
    })
  );
  private selectedIndustriesSubject = new BehaviorSubject<IndustryViewModel[]>([]);
  selectedIndustries$ = this.selectedIndustriesSubject.asObservable().pipe(
    map(industries => {
      return industries.map(this.mapIndustry);
    })
  );
  private mapIndustry(industry: IndustryViewModel): FilterValue {
    return { id: industry.id, name: industry.name, color: filterColors.industry, type: FilterType.industry };
  }

  private categoriesSubject = new BehaviorSubject<AssetCategoryViewModel[]>([]);
  categories$ = this.categoriesSubject.asObservable().pipe(
    map(categories => {
      return categories.map(this.mapCategory);
    })
  );
  private selectedCategoriesSubject = new BehaviorSubject<AssetCategoryViewModel[]>([]);
  selectedCategories$ = this.selectedCategoriesSubject.asObservable().pipe(
    map(categories => {
      return categories.map(this.mapCategory);
    })
  );
  private mapCategory(category: AssetCategoryViewModel): FilterValue {
    return { id: category.id, name: category.name, color: filterColors.category, type: FilterType.category };
  }

  private searchTextSubject = new BehaviorSubject<string>("");
  searchText$ = this.searchTextSubject.asObservable();

  private demoAssetsSubject = new BehaviorSubject<AssetGroupViewModel[]>([]);
  demoAssets$ = combineLatest([
    this.selectedMacrosectors$,
    this.selectedDomains$,
    this.selectedProcess$,
    this.selectedUseCases$,
    this.selectedFunctionalities$,
    this.selectedIndustries$,
    this.searchText$,
    this.demoAssetsSubject.asObservable()
  ]).pipe(
    map(([macrosectors, domains, processes, useCases, functionalities, industries, searchText, groupAssets]) => {
      return groupAssets.map(group => ({
        ...group,
        assets: group.assets.filter(asset =>
          this.checkAssetVisible(
            asset,
            macrosectors.map(x => x.id),
            domains.map(x => x.id),
            processes.map(x => x.id),
            useCases.map(x => x.id),
            functionalities.map(x => x.id),
            industries.map(x => x.id),
            searchText
          )
        )
      }));
    })
  );

  private demoTemplateSubject = new BehaviorSubject<DemoTemplateViewModel[]>([]);
  demoTemplates$ = combineLatest([
    this.selectedMacrosectors$,
    this.selectedDomains$,
    this.selectedProcess$,
    this.selectedUseCases$,
    this.selectedFunctionalities$,
    this.selectedIndustries$,
    this.searchText$,
    this.demoTemplateSubject.asObservable()
  ]).pipe(
    map(([macrosectors, domains, processes, useCases, functionalities, industries, searchText, templates]) => {
      return templates.filter(template =>
        template.relatedDemoAssets?.some(asset =>
          this.checkAssetVisible(
            asset,
            macrosectors.map(x => x.id),
            domains.map(x => x.id),
            processes.map(x => x.id),
            useCases.map(x => x.id),
            functionalities.map(x => x.id),
            industries.map(x => x.id),
            searchText
          )
        )
      );
    })
  );

  filters$ = combineLatest([
    this.selectedMacrosectors$,
    this.selectedDomains$,
    this.selectedProcess$,
    this.selectedUseCases$,
    this.selectedFunctionalities$,
    this.selectedIndustries$,
    this.selectedCategories$
  ]).pipe(
    map(([macrosectors, domains, processes, useCases, functionalities, industries, categories]) => {
      return [...macrosectors, ...domains, ...processes, ...industries, ...useCases, ...functionalities, ...categories];
    })
  );

  constructor(
    private metadataService: MetadataService,
    private demoAssetService: DemoAssetService,
    private templateService: DemoTemplateService
  ) {}

  initFilters(): Observable<FilterViewModel> {
    return this.metadataService.getFilterViewModel().pipe(
      map(res => {
        this.macrosectorsSubject.next(res.macrosectors);
        this.domainsSubject.next(res.domains);
        this.processSubject.next(res.processes);
        this.useCasesSubject.next(res.useCases);
        this.functionalitiesSubject.next(res.functionalities);
        this.industriesSubject.next(res.industries);
        this.categoriesSubject.next(res.categories);

        return res;
      })
    );
  }

  initDemoAssets(): Observable<AssetGroupViewModel[]> {
    return this.demoAssetService.getAssets(true, false, true).pipe(
      map(res => {
        const mapGroupAssets: Map<number, AssetGroupViewModel> = new Map();
        mapGroupAssets.set(0, {
          categoryId: 0,
          categoryName: "No Category",
          categoryDescription: "",
          assets: [],
          categoryOrder: 0
        } as AssetGroupViewModel);
        const grouppedAssets = res.reduce((groupMap, asset) => {
          const catId = asset.categoryId ?? 0;
          if (!groupMap.has(catId)) {
            const category = asset.category!;
            groupMap.set(catId, {
              categoryId: catId,
              categoryName: category.name,
              categoryDescription: category.description,
              categoryColor: category.color,
              categoryIcon: category.icon,
              categoryOrder: category.order,
              assets: []
            } as AssetGroupViewModel);

            //increase No category order
            const noCategory = mapGroupAssets.get(0)!;
            noCategory.categoryOrder += 1;
          }
          const mapValue = groupMap.get(catId) as AssetGroupViewModel;
          mapValue.assets.push({
            id: asset.id,
            name: asset.name,
            description: asset.description,
            ramConsumption: asset.ramConsumption,
            diskConsumption: asset.diskConsumption,
            suggestedDemos: asset.suggestedDemos,
            useCases: asset.useCases ?? [],
            processes: asset.processes ?? [],
            domains: asset.domains ?? [],
            functionalities: asset.functionalities ?? [],
            default: asset.default,
            industries: asset.industries ?? []
          });

          return groupMap;
        }, mapGroupAssets);

        const noCategoryGroup = mapGroupAssets.get(0)!;
        if (!noCategoryGroup.assets.length) mapGroupAssets.delete(0);

        const demoTile = [...grouppedAssets.values()];
        demoTile.sort((a, b) => a.categoryOrder - b.categoryOrder);
        this.demoAssetsSubject.next(demoTile);
        return demoTile;
      })
    );
  }

  initDemoTemplates(): Observable<DemoTemplateViewModel[]> {
    return this.templateService.getActiveTempaltes().pipe(
      map(res => {
        this.demoTemplateSubject.next(res);
        return res;
      })
    );
  }

  updateSearch(searchText: string): void {
    this.searchTextSubject.next(searchText);
  }

  addFilter(filter: FilterValue): void {
    switch (filter.type) {
      case FilterType.macrosector:
        this.addSelectedSubjects<MacrosectorViewModel>(this.macrosectorsSubject, this.selectedMacrosectorsSubject, filter.id);
        break;
      case FilterType.domain:
        this.addFilterDomain(filter.id);
        break;
      case FilterType.process:
        this.addFilterProcess(filter.id);
        break;
      case FilterType.useCase:
        this.addFilterUseCase(filter.id);
        break;
      case FilterType.functionality:
        this.addSelectedSubjects<FunctionalityViewModel>(this.functionalitiesSubject, this.selectedFunctionalitiesSubject, filter.id);
        break;
      case FilterType.industry:
        this.addSelectedSubjects<IndustryViewModel>(this.industriesSubject, this.selectedIndustriesSubject, filter.id);
        break;
      case FilterType.category:
        this.addSelectedSubjects<AssetCategoryViewModel>(this.categoriesSubject, this.selectedCategoriesSubject, filter.id);
        break;
    }
  }

  removeFilter(filter: FilterValue): void {
    switch (filter.type) {
      case FilterType.macrosector:
        this.removeFilterMacrosector(filter.id);
        break;
      case FilterType.domain:
        this.removeFilterDomain(filter.id);
        break;
      case FilterType.process:
        this.removeFilterProcess([filter.id]);
        break;
      case FilterType.useCase:
        this.removeSelectedSubjects<UseCaseViewModel>(this.selectedUseCasesSubject, [filter.id]);
        break;
      case FilterType.functionality:
        this.removeSelectedSubjects<FunctionalityViewModel>(this.selectedFunctionalitiesSubject, [filter.id]);
        break;
      case FilterType.industry:
        this.removeSelectedSubjects<IndustryViewModel>(this.selectedIndustriesSubject, [filter.id]);
        break;
      case FilterType.category:
        this.removeSelectedSubjects<AssetCategoryViewModel>(this.selectedCategoriesSubject, [filter.id]);
        break;
    }
  }

  private addSelectedSubjects<T extends { id: number }>(
    subject: BehaviorSubject<T[]>,
    selectSubject: BehaviorSubject<T[]>,
    newItemId: number
  ): T {
    const item = subject.value.find(x => x.id === newItemId)!;
    if (!selectSubject.value.includes(item)) {
      selectSubject.next([...selectSubject.value, item]);
    }

    return item;
  }

  private addFilterProcess(processId: number): void {
    const process = this.addSelectedSubjects<ProcessViewModel>(this.processSubject, this.selectedProcessSubject, processId);
    this.addFilterDomain(process.domainId);
  }

  private addFilterUseCase(useCaseId: number): void {
    const useCase = this.addSelectedSubjects<UseCaseViewModel>(this.useCasesSubject, this.selectedUseCasesSubject, useCaseId);
    this.addFilterProcess(useCase.processId);
  }

  private addFilterDomain(domainId: number): void {
    const domain = this.addSelectedSubjects<DomainViewModel>(this.domainsSubject, this.selectedDomainsSubject, domainId);
    this.addSelectedSubjects<MacrosectorViewModel>(this.macrosectorsSubject, this.selectedMacrosectorsSubject, domain.macrosectorId);
  }

  private removeSelectedSubjects<T extends { id: number }>(selectSubject: BehaviorSubject<T[]>, itemIds: number[]): void {
    const items = selectSubject.value.filter(x => !itemIds.includes(x.id));
    selectSubject.next(items);
  }

  private removeFilterProcess(processIds: number[]): void {
    this.removeSelectedSubjects<ProcessViewModel>(this.selectedProcessSubject, processIds);
    this.removeSelectedSubjects<UseCaseViewModel>(
      this.selectedUseCasesSubject,
      this.selectedUseCasesSubject.value.filter(x => processIds.includes(x.processId)).map(x => x.id)
    );
  }

  private removeFilterDomain(domainId: number): void {
    this.removeSelectedSubjects<DomainViewModel>(this.selectedDomainsSubject, [domainId]);
    this.removeFilterProcess(this.selectedProcessSubject.value.filter(x => x.domainId === domainId).map(x => x.id));
  }

  private removeFilterMacrosector(macrosectorId: number): void {
    const domainIds = this.selectedDomainsSubject.value.filter(x => x.macrosectorId === macrosectorId).map(x => x.id);

    this.removeSelectedSubjects<MacrosectorViewModel>(this.selectedMacrosectorsSubject, [macrosectorId]);
    this.removeSelectedSubjects<DomainViewModel>(this.selectedDomainsSubject, domainIds);
    this.removeFilterProcess(this.selectedProcessSubject.value.filter(x => x.macrosectorId === macrosectorId).map(x => x.id));
    this.removeSelectedSubjects<UseCaseViewModel>(
      this.selectedUseCasesSubject,
      this.selectedUseCasesSubject.value.filter(x => x.macrosectorId === macrosectorId).map(x => x.id)
    );
  }

  private checkAssetVisible(
    asset: DemoTileViewModel,
    macrosectorIds: number[],
    domainIds: number[],
    processIds: number[],
    useCaseIds: number[],
    funcIds: number[],
    indIds: number[],
    searchText: string
  ): boolean {
    let res =
      !macrosectorIds.length ||
      macrosectorIds.every(macrosectorId =>
        asset.domains.some(domainId =>
          this.domainsSubject.value.some(domain => domain.id === domainId && domain.macrosectorId === macrosectorId)
        )
      );
    res = res && (!domainIds.length || domainIds.every(d => asset.domains.includes(d)));
    res = res && (!processIds.length || processIds.every(p => asset.processes.includes(p)));
    res = res && (!useCaseIds.length || useCaseIds.every(u => asset.useCases.includes(u)));
    res = res && (!funcIds.length || funcIds.every(f => asset.functionalities.includes(f)));
    res = res && (!indIds.length || !asset.industries?.length || indIds.every(i => asset.industries?.map(x => x.id).includes(i)));

    if (searchText)
      res =
        res &&
        (asset.name.toLowerCase().includes(searchText.toLowerCase()) || asset.description.toLowerCase().includes(searchText.toLowerCase()));

    return res;
  }
}
